import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SocialAuthButtons from '../components/SocialAuthButtons';
// Login.css es la base compartida de las tres pantallas de auth (login,
// registro y recuperar contraseña). Antes Register.css repetía 30 de sus reglas
// y, como no hay code splitting, las dos hojas eran globales: la que cargara
// último ganaba. Eso hacía que la pantalla de login se pintara con estilos de
// registro, y que tocar Login.css no tuviera efecto.
import './Login.css';
import './Register.css';
import FondoDeGranos, { FONDO_AUTH_ANCHO, FONDO_AUTH_ANGOSTO, RESPALDO_AUTH, usePantallaAncha } from '../components/business/FondoDeGranos';

const Register = () => {
  const pantallaAncha = usePantallaAncha();
  const navigate = useNavigate();
  const { signup, resendEmailVerification, refreshCurrentUser, currentUser, userData, loading } = useAuth();
  
  // Redirect authenticated users
  //
  // Mismo guard que en Login.jsx: sin `userData` en la condición, alguien con la
  // sesión a medio cargar se va al onboarding aunque ya lo haya completado.
  useEffect(() => {
    if (loading || !currentUser || !userData) return;

    console.log('🔄 Register: sesión resuelta, redirigiendo...');
    navigate(userData.onboardingCompleted ? '/admin/dashboard' : '/admin/onboarding', {
      replace: true,
    });
  }, [currentUser, userData, loading, navigate]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumbers: false
  });

  const validateForm = () => {
    const newErrors = {};

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = "El email es requerido";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Ingresá un email válido";
    }

    // Validar contraseña
    if (!formData.password) {
      newErrors.password = "La contraseña es requerida";
    } else {
      // Verificar que todos los requisitos se cumplan
      const allRequirementsMet = passwordRequirements.minLength && 
                                passwordRequirements.hasUpperCase && 
                                passwordRequirements.hasLowerCase && 
                                passwordRequirements.hasNumbers;
      
      if (!allRequirementsMet) {
        newErrors.password = "La contraseña debe cumplir todos los requisitos";
      }
    }

    // Validar confirmación de contraseña
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirmá tu contraseña";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Las contraseñas no coinciden";
    }

    // Validar términos y condiciones
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = "Debes aceptar los términos y condiciones";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Si es el campo de contraseña, actualizar los requisitos en tiempo real
    if (name === 'password') {
      setPasswordRequirements({
        minLength: value.length >= 8,
        hasUpperCase: /[A-Z]/.test(value),
        hasLowerCase: /[a-z]/.test(value),
        hasNumbers: /\d/.test(value)
      });
    }
    
    // Si es confirmación de contraseña, verificar si coincide
    if (name === 'confirmPassword') {
      if (value && formData.password) {
        if (value !== formData.password) {
          setErrors(prev => ({
            ...prev,
            confirmPassword: "Las contraseñas no coinciden"
          }));
        } else {
          setErrors(prev => ({
            ...prev,
            confirmPassword: ""
          }));
        }
      } else if (value) {
        // Si hay algo en confirmPassword pero no hay password principal
        setErrors(prev => ({
          ...prev,
          confirmPassword: "Las contraseñas no coinciden"
        }));
      }
    }
    
    // También verificar cuando se cambia la contraseña principal
    if (name === 'password' && formData.confirmPassword) {
      if (value !== formData.confirmPassword) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: "Las contraseñas no coinciden"
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          confirmPassword: ""
        }));
      }
    }
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    // PERO NO limpiar el error de confirmPassword si las contraseñas siguen sin coincidir
    if (errors[name] && name !== 'confirmPassword') {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Función para verificar si el formulario puede ser enviado
  const canSubmitForm = () => {
    const allPasswordRequirementsMet = passwordRequirements.minLength && 
                                     passwordRequirements.hasUpperCase && 
                                     passwordRequirements.hasLowerCase && 
                                     passwordRequirements.hasNumbers;
    
    const passwordsMatch = formData.password === formData.confirmPassword;
    const noConfirmPasswordError = !errors.confirmPassword;
    
    return formData.email && 
           allPasswordRequirementsMet && 
           formData.confirmPassword && 
           passwordsMatch &&
           noConfirmPasswordError &&
           formData.acceptTerms;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const defaultBusinessName = "Mi Negocio";
      // Detectar zona horaria del navegador
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Argentina/Buenos_Aires';
      // Registrar con Firebase y guardar zona horaria
      const userCredential = await signup(formData.email, formData.password, defaultBusinessName, timeZone);
      setEmailVerificationSent(true);
      // NO navegar al dashboard aquí. El usuario debe verificar su email primero.
    } catch (err) {
      
      // Manejar errores de Firebase
      let errorMessage = "Error al crear la cuenta";
      
      switch (err.code) {
        case "auth/email-already-in-use":
          errorMessage = "Ya existe una cuenta con este email";
          break;
        case "auth/invalid-email":
          errorMessage = "Email inválido";
          break;
        case "auth/operation-not-allowed":
          errorMessage = "Registro deshabilitado temporalmente";
          break;
        case "auth/weak-password":
          errorMessage = "La contraseña es muy débil";
          break;
        case "auth/network-request-failed":
          errorMessage = "Error de conexión. Verifica tu conexión a internet";
          break;
        default:
          errorMessage = err.message || "Error al crear la cuenta";
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler para reenviar email de verificación
  const handleResendVerification = async () => {
    setIsSubmitting(true);
    try {
      await resendEmailVerification();
      setEmailVerificationSent(true);
    } catch (err) {
      setErrors({ general: 'Error al reenviar el email de verificación' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler para chequear si el email fue verificado
  const handleCheckVerification = async () => {
    setIsSubmitting(true);
    setErrors({}); // Limpiar errores previos
    
    try {
      // Refrescar el usuario para obtener el estado más reciente
      await refreshCurrentUser();
      
      // Obtener el usuario actual
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        setErrors({ general: 'No hay usuario autenticado. Intenta iniciar sesión nuevamente.' });
        return;
      }
      
      if (user.emailVerified) {
        setEmailVerified(true);
        // Navegar al onboarding después de un breve delay para mostrar el mensaje de éxito
        setTimeout(() => {
          navigate('/admin/onboarding');
        }, 1500);
      } else {
        setErrors({ general: 'El email aún no ha sido verificado. Revisa tu bandeja de entrada y spam.' });
      }
    } catch (err) {
      console.error('Error checking verification:', err);
      setErrors({ general: 'Error al chequear verificación. Intenta de nuevo.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* El mismo fondo que la landing dos, y el mismo componente. Antes estas tres
     pantallas tenían su propio degradado más tres esferas borroseadas que no
     existían en ningún otro lado de la app, así que pasar de la landing a
     "Ingresar" cambiaba de fondo sin motivo.

     `FondoDeGranos` va adentro de `.auth-container` —que ya es `position:
     relative` y `overflow: hidden`— y la tarjeta queda por encima gracias al
     z-index que ya tenía `.auth-content`. */
  return (
    <div className="auth-container">
      <FondoDeGranos
        posicion={pantallaAncha ? FONDO_AUTH_ANCHO : FONDO_AUTH_ANGOSTO}
        respaldo={RESPALDO_AUTH}
      />

      <div className="auth-content">
        <div className="auth-card">
          <div className="auth-header">
            <Link to="/" className="logo-link">
              <img 
                src="/logo-aturno.png" 
                alt="Aturno Logo" 
                className="auth-logo"
              />
            </Link>
            {/* El encabezado acompaña el estado: una vez creada la cuenta,
                seguir diciendo "Creá tu cuenta gratis" desorienta. */}
            <h2>{emailVerificationSent ? 'Confirmá tu email' : 'Creá tu cuenta gratis'}</h2>
            {/* La bajada queda solo para el paso de verificación, donde explica
                qué falta. La otra —"Empezá a gestionar tus turnos en minutos"—
                era texto de venta en una pantalla que ya solo pide mail y
                contraseña: acá el visitante ya decidió registrarse. */}
            {emailVerificationSent && <p>Es el último paso antes de empezar</p>}
          </div>

          {emailVerificationSent ? (
            /* Esta pantalla no tenía una sola línea de CSS: eran un h3, un
               párrafo y dos botones sueltos sin layout ni espaciado. Ahora usa
               las mismas clases que el resto de las pantallas de auth
               (auth-form, form-group, auth-submit, btn-secondary), así que
               hereda la tipografía, los colores y los botones de la app. */
            <div className="auth-form verification-panel">
              <div className="verification-icon" aria-hidden="true">📬</div>

              <p className="verification-lead">
                Te mandamos un email a{' '}
                <strong className="verification-email">{formData.email}</strong> con un link para
                confirmar tu cuenta.
              </p>

              <ul className="verification-tips">
                <li>Puede tardar unos minutos en llegar.</li>
                <li>Si no lo ves, revisá la carpeta de spam o promociones.</li>
              </ul>

              {emailVerified ? (
                <div className="verification-ok" role="status">
                  <span aria-hidden="true">✅</span>
                  ¡Listo! Te estamos llevando a tu panel...
                </div>
              ) : (
                <div className="verification-actions">
                  <button
                    onClick={handleCheckVerification}
                    disabled={isSubmitting}
                    className="btn-primary auth-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Verificando...
                      </>
                    ) : (
                      'Ya confirmé mi email'
                    )}
                  </button>
                  <button
                    onClick={handleResendVerification}
                    disabled={isSubmitting}
                    className="btn-secondary verification-resend"
                  >
                    Volver a enviarlo
                  </button>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              {errors.general && (
                <div className="error-banner">
                  {errors.general}
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="tu@email.com"
                  className={errors.email ? "error" : ""}
                  autoComplete="email"
                />
                {errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password">Contraseña</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Tu contraseña"
                    className={errors.password ? "error" : ""}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && (
                  <span className="error-message">{errors.password}</span>
                )}
                
                {/* Los requisitos aparecen recién cuando se empieza a escribir.
                    Antes estaban siempre: al entrar a la página te recibían
                    cuatro cruces rojas marcando como error algo que todavía no
                    habías tenido oportunidad de hacer, y ocupaban 150px. */}
                {formData.password && (
                <div className="password-requirements">
                  <div className={`requirement ${passwordRequirements.minLength ? 'met' : 'unmet'}`}>
                    <span className="requirement-icon">{passwordRequirements.minLength ? '✓' : '✗'}</span>
                    Mínimo 8 caracteres
                  </div>
                  <div className={`requirement ${passwordRequirements.hasUpperCase ? 'met' : 'unmet'}`}>
                    <span className="requirement-icon">{passwordRequirements.hasUpperCase ? '✓' : '✗'}</span>
                    Al menos una mayúscula
                  </div>
                  <div className={`requirement ${passwordRequirements.hasLowerCase ? 'met' : 'unmet'}`}>
                    <span className="requirement-icon">{passwordRequirements.hasLowerCase ? '✓' : '✗'}</span>
                    Al menos una minúscula
                  </div>
                  <div className={`requirement ${passwordRequirements.hasNumbers ? 'met' : 'unmet'}`}>
                    <span className="requirement-icon">{passwordRequirements.hasNumbers ? '✓' : '✗'}</span>
                    Al menos un número
                  </div>
                </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmar contraseña</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Repetí tu contraseña"
                    className={errors.confirmPassword ? "error" : ""}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span className="error-message">{errors.confirmPassword}</span>
                )}
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    className={errors.acceptTerms ? "error" : ""}
                  />
                  <span className="checkbox-text">
                    Acepto los{" "}
                    <Link to="/legal/terms" target="_blank" className="terms-link">
                      términos y condiciones
                    </Link>{" "}
                    y la{" "}
                    <Link to="/legal/privacy" target="_blank" className="terms-link">
                      política de privacidad
                    </Link>
                  </span>
                </label>
                {errors.acceptTerms && (
                  <span className="error-message">{errors.acceptTerms}</span>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary auth-submit"
                disabled={isSubmitting || !canSubmitForm()}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Creando cuenta...
                  </>
                ) : (
                  "Crear cuenta gratis"
                )}
              </button>
            </form>
          )}

          <SocialAuthButtons 
            onSuccess={(result) => {
              console.log('Registro social exitoso:', result);
              navigate('/admin/onboarding');
            }}
            onError={(error) => {
              console.error('Error en registro social:', error);
              setErrors({ general: error });
            }}
          />

          <div className="auth-footer">
            <p>
              ¿Ya tenés cuenta?{" "}
              <Link to="/auth/login" className="register-link">
                Iniciá sesión
              </Link>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Register; 